import { ApolloClient } from '@apollo/client';

import { TsRestClient } from '@eluve/api-client-provider';
import { ScheduleBackgroundTaskAction } from '@eluve/appointment-tasks';
import { isValidTimestamp } from '@eluve/date-utils';
import { createDefaultLogActionFromCorrelatedLogger } from '@eluve/frontend-machine-utils';
import { CorrelatedLogger } from '@eluve/logger';
import { recordingTimestampFromFileName, sleep } from '@eluve/utils';

import { defaultPresignedUploadMachine } from './defaultPresignedUrlUploadMachine';
import { DexieDb } from './files-db';
import { createPeriodicFileMachineLogic } from './periodicRemoteFileSync.machine';
import { convertPresignedUrlMachineToCallback } from './presignedUrlUpload.machine';
import { convertUploadFileMachineToCallback } from './uploadFile.machine';
import { UserFileSystem } from './user-file-system.store';

type Args = {
  apiClient: TsRestClient;
  apolloClient: ApolloClient<unknown>;
  db: DexieDb;
  userFileSystem: UserFileSystem;
  log: CorrelatedLogger;
  fileRetrievalDelayInMs?: number;
  clientOrigin?: string;
  scheduleBackgroundTask: ScheduleBackgroundTaskAction;
};

export const makeDefaultRemoteFileSyncMachineLogic = (args: Args) => {
  const {
    apiClient,
    apolloClient,
    db,
    log,
    userFileSystem,
    fileRetrievalDelayInMs,
    clientOrigin,
    scheduleBackgroundTask,
  } = args;

  const logic = createPeriodicFileMachineLogic({
    actions: {
      log: createDefaultLogActionFromCorrelatedLogger(log),
    },
    actors: {
      updateLastAttemptedAt: async ({
        id,
        attemptCount,
      }: {
        id: string;
        attemptCount: number;
      }) => {
        await db.appointmentFiles.update(id, {
          lastUploadAttemptedAt: new Date().toISOString(),
          attemptCount,
        });
      },
      getFileToSync: async () => {
        const syncableFiles = await db.appointmentFiles
          .orderBy('lastUploadAttemptedAt')
          .filter((x) => isValidTimestamp(recordingTimestampFromFileName(x.id)))
          .toArray();

        const file = syncableFiles?.[0];

        if (file) {
          log('info', 'Found a file to sync', {
            fileName: file.id,
            appointmentId: file.appointmentId,
          });

          const newAttemptCount = file.attemptCount + 1;
          await db.appointmentFiles.update(file.id, {
            lastUploadAttemptedAt: new Date().toISOString(),
            attemptCount: newAttemptCount,
          });
        }

        return file;
      },
      uploadFile: convertUploadFileMachineToCallback({
        logger: log,
        getFile: async ({ appointmentId, fileName }) => {
          log('info', 'Attempting to read file from user file system', {
            appointmentId,
            fileName,
          });
          if (userFileSystem.type === 'available') {
            const appointmentDir =
              await userFileSystem.appointmentsDir.getDirectoryHandle(
                appointmentId,
              );

            if (fileRetrievalDelayInMs) {
              await sleep(fileRetrievalDelayInMs);
            }

            const file = await appointmentDir.getFileHandle(fileName);
            const fileData = await file.getFile();
            return fileData;
          }

          throw new Error('User file system is not available');
        },
        uploadFile: convertPresignedUrlMachineToCallback(
          defaultPresignedUploadMachine({
            apiClient,
            log,
            apolloClient,
            clientOrigin,
            scheduleBackgroundTask,
          }),
        ),
        cleanupFile: async ({ appointmentId, fileId }) => {
          log('info', 'Removing file', { appointmentId, fileId });
          if (userFileSystem.type === 'available') {
            await db.appointmentFiles.delete(fileId);
            const appointmentDir =
              await userFileSystem.appointmentsDir.getDirectoryHandle(
                appointmentId,
              );

            await appointmentDir.removeEntry(fileId).catch((e) => {
              log('warn', 'Failed to remove file entry', { fileId, error: e });
            });

            log('info', 'Finished cleaning up file', { fileId });
          } else {
            throw new Error('User file system is not available');
          }
        },
      }),
    },
  });

  return logic;
};
